import React from 'react';
import { GlobeIcon, LinkedinIcon, MapPinIcon, NewspaperIcon, SendIcon } from 'lucide-react';
import { NetworkingGuest } from '../../data/networking-guests';
import { GOLD, NetworkingCopy } from '../../data/networking-copy';
function initialsOf(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
}
const linkChip = 'inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold text-white/75 border border-white/15 transition-colors hover:border-white/35 hover:text-white';

/**
 * One guest in the directory. Memoized so typing in search re-renders only the
 * input, not the whole roster.
 */
export const GuestCard = React.memo(function GuestCard({
  guest,
  c,
  priority,
  onRequest
}: {
  guest: NetworkingGuest;
  c: NetworkingCopy;
  /** Cards in the first visible row load their portrait immediately. */
  priority: boolean;
  onRequest: (guest: NetworkingGuest) => void;
}) {
  return <article className="group flex flex-col h-full rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden transition-colors duration-200 hover:border-[#c9a227]/40 hover:bg-white/[0.05]" style={priority ? undefined :
  // Skip layout and paint for cards that are scrolled out of view.
  {
    contentVisibility: 'auto',
    containIntrinsicSize: '520px'
  }} data-id="element-2943" data-mp="3rn6xf9igw0v7">
      {/* Portrait fills the top of the card so faces read at a glance */}
      <div className="relative w-full aspect-square bg-white/[0.06] overflow-hidden" data-id="element-2944" data-mp="acz6b5ejw31d">
        {guest.photo ? <img src={guest.photo} alt={guest.name} loading={priority ? 'eager' : 'lazy'} fetchPriority={priority ? 'high' : 'low'} decoding="async" width={640} height={640} className="absolute inset-0 w-full h-full object-cover object-[center_22%]" style={guest.photoZoom || guest.photoFilter ? {
        ...(guest.photoZoom && {
          transform: `scale(${guest.photoZoom})`,
          transformOrigin: guest.photoFocus ?? 'center 30%'
        }),
        ...(guest.photoFilter && {
          filter: guest.photoFilter
        })
      } : undefined} data-id="element-2945" data-mp="o4403qv8m13s" /> : <div className="absolute inset-0 flex items-center justify-center" data-id="element-2946" data-mp="1jv5w3mdx99vj">
            <span className="font-bebas text-5xl tracking-wider text-white/25" data-id="element-2947" data-mp="1nw9cc9zvt7xa">
              {initialsOf(guest.name)}
            </span>
          </div>}
        {(() => {
        const tags = [guest.sector, guest.secondarySector, ...(guest.extraSectors ?? [])].filter(Boolean) as string[];
        // Two tags sit in the top corner; any beyond that move to the bottom
        // edge so they never wrap across the face.
        const top = tags.slice(0, 2);
        const bottom = tags.slice(2);
        const pill = (tag: string) => <span key={tag} className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] text-[#0b1f3f] shadow-sm" style={{
          backgroundColor: GOLD
        }} data-id="element-2948" data-mp="3nbu2ozqzlhow">
              {tag}
            </span>;
        return <>
              <div className="absolute top-3 left-3 flex flex-wrap gap-1.5" data-id="element-2949" data-mp="ewaggkoqgzjc">
                {top.map(pill)}
              </div>
              {bottom.length > 0 && <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-1.5" data-id="element-2950" data-mp="4xthb0fn5em7">
                  {bottom.map(pill)}
                </div>}
            </>;
      })()}
        {guest.boardOrder !== undefined && <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] text-white bg-[#0b1f3f]/85 ring-1 ring-white/20" data-id="element-2951" data-mp="2pmr1c4ukn6po">
            {c.boardBadge}
          </span>}
      </div>

      <div className="p-6 flex-1 flex flex-col" data-id="element-2952" data-mp="i5scr8jq71ur">
        <h3 className="font-bebas text-3xl tracking-wider text-white leading-none" data-id="element-2953" data-mp="i6lhk3z4jha9">
          {guest.name}
        </h3>
        {guest.headline && <p className="mt-2 text-xs font-bold uppercase tracking-[0.12em] leading-snug text-white" data-id="element-2954" data-mp="8eo1ojx68157">
            {guest.headline}
          </p>}
        {guest.roles && guest.roles.length > 0 && <ul className="mt-3 space-y-2.5" data-id="element-2955" data-mp="114ef0qqzaz09">
            {guest.roles.map(role => <li key={`${role.title}-${role.organization}`} data-id="element-2956" data-mp="m7dr6ybwro72">
                <p className="text-sm font-semibold text-white/80 leading-snug" data-id="element-2957" data-mp="m989s5zpj47z">
                  {role.title}
                </p>
                <p className="text-sm text-white/50 leading-snug" data-id="element-2958" data-mp="36wl7u96e2lp7">
                  {role.organization}
                </p>
              </li>)}
          </ul>}
        {!guest.roles && guest.title && <p className="mt-2 text-sm font-semibold text-white/80 leading-snug" data-id="element-2959" data-mp="1tx9ma8iotw5e">
            {guest.title}
          </p>}
        {!guest.roles && guest.organization && <p className="text-sm text-white/50 leading-snug" data-id="element-2960" data-mp="22ziwexa1nni9">
            {guest.organization}
          </p>}
        {guest.location && <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-white/45" data-id="element-2961" data-mp="1yvji8lc8l0s2">
            <MapPinIcon className="w-3.5 h-3.5" data-id="element-2962" data-mp="1b8l805epmp2l" />
            {guest.location}
          </p>}

        {guest.bio && <p className="mt-4 text-sm leading-relaxed text-white/65" data-id="element-2963" data-mp="1675jg3dbq0fg">
            {guest.bio}
          </p>}

        {guest.lookingFor && <div className="mt-5 rounded-xl border border-[#c9a227]/20 bg-[#c9a227]/[0.07] px-4 py-3" data-id="element-2964" data-mp="1f8lri4go1qgi">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1.5" style={{
          color: GOLD
        }} data-id="element-2965" data-mp="rirjc07akq2l">
              {c.lookingFor}
            </p>
            <p className="text-sm leading-relaxed text-white/75" data-id="element-2966" data-mp="2mm7gzyqof7my">
              {guest.lookingFor}
            </p>
          </div>}

        <div className="mt-auto pt-6" data-id="element-2967" data-mp="2rk8vs7h2kksm">
          <div className="flex flex-wrap items-center gap-2" data-id="element-2968" data-mp="2wcilpni6pkuw">
            {guest.linkedin && <a href={guest.linkedin} target="_blank" rel="noopener noreferrer" className={linkChip} data-id="element-2969" data-mp="31codr4sd99di">
                <LinkedinIcon className="w-3.5 h-3.5" data-id="element-2970" data-mp="28n7qeljlsio5" />
                {c.linkedin}
              </a>}
            {guest.website && <a href={guest.website} target="_blank" rel="noopener noreferrer" className={linkChip} data-id="element-2971" data-mp="2dkzh5c8ihyqr">
                <GlobeIcon className="w-3.5 h-3.5" data-id="element-2972" data-mp="3p0o5o81ez5to" />
                {c.website}
              </a>}
            {guest.additionalLinks?.map(link => <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer" className={linkChip} data-id="element-2973" data-mp="1id7b60qbjbpg">
                <GlobeIcon className="w-3.5 h-3.5" data-id="element-2974" data-mp="18ndx8t2peki1" />
                {link.label}
              </a>)}
            {guest.feature && <a href={guest.feature} target="_blank" rel="noopener noreferrer" className={linkChip} data-id="element-2975" data-mp="13o1ccdve7ni3">
                <NewspaperIcon className="w-3.5 h-3.5" data-id="element-2976" data-mp="un37xuh1ldyz" />
                {c.feature}
              </a>}
          </div>
          {guest.acceptsIntros !== false && <button onClick={() => onRequest(guest)} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-bold text-[#0b1f3f] transition-transform duration-150 hover:scale-[1.01]" style={{
          backgroundColor: GOLD
        }} data-id="element-2977" data-mp="kq8e3ovps1r1">
              <SendIcon className="w-4 h-4" data-id="element-2978" data-mp="1nzoqyjmcxv4i" />
              {c.requestIntro}
            </button>}
        </div>
      </div>
    </article>;
});